import { isSoldOutPlaceholder } from '@app/model/company_hotel_room_mappings_type_configuration/isSoldOutPlaceholder';
import CompanyHotelParsingSetting from '@app/model/company_hotel/CompanyHotelParsingSetting';
import HotelWithRoomMappings from '@app/model/company_hotel/HotelWithRoomMappings';
import CompanyHotelSettings from '@app/model/company_hotel/settings/CompanyHotelSettings';
import Competitor from '@app/model/company_hotel/Competitor';
import { Module } from '@app/model/company_hotel/Module';
import CompetitorType from '@app/model/company_hotel/CompetitorType';
import HotelRoomMappingType from '@app/model/market_state/HotelRoomMappingType';
import DataProvider from '@app/model/_common/DataProvider';
import RoomMappingTypeConfigurationOutput
	from '@app/model/company_hotel_room_mappings_type_configuration/RoomMappingTypeConfigurationOutput';
import CompetitorTag from '@app/model/company_hotel/CompetitorTag';
import CompanyHotelMetaradarSettings from '@app/model/company_hotel/CompanyHotelMetaradarSettings';
import Utilities from '@util/Utilities';
import Domain from '@app/model/_common/Domain';
import CompanyHotelParsingSettingState from '@app/model/company_hotel/CompanyHotelParsingSettingState';
import Hotel from '@app/model/hotel/Hotel';
import CompanyHotelPriceRecommendationSettings
	from '@app/model/company_hotel/settings/CompanyHotelPriceRecommendationSettings';
import CompanyHotelPriceRecommendationEngine from '@app/model/company_hotel/CompanyHotelPriceRecommendationEngine';
import CompanyHotelPriceRecommendationEngineVersion
	from '@app/model/company_hotel/CompanyHotelPriceRecommendationEngineVersion';
import CalendarSettingsComparisonMethod from '@app/model/company_hotel/settings/CalendarSettingsComparisonMethod';

export default class CompanyHotel {
	id: number;
	pricingIntegrationActive: boolean;
	webhotelierId?: string;
	mainHotel: Competitor;
	competitors: Competitor[];
	settings: CompanyHotelSettings;
	metaradarSettings?: CompanyHotelMetaradarSettings;
	parsingSettings: CompanyHotelParsingSetting[];
	roomMappingTypeConfigurations: RoomMappingTypeConfigurationOutput[];
	priceRecommendationSettings: CompanyHotelPriceRecommendationSettings;
	competitorTags: CompetitorTag[];
	allHotels: Hotel[];
	allHotelCompetitors: HotelWithRoomMappings[];
	allGroupCompetitors: HotelWithRoomMappings[];

	constructor(data: CompanyHotel) {
		Object.assign(this, data);
		this.allHotels = this.getAllHotels();
		this.allHotelCompetitors = this.getAllHotelCompetitors();
		this.allGroupCompetitors = this.getAllGroupCompetitors();
	}

	public usesWeightedAverage() {
        return this.settings.calendar.comparisonMethod === CalendarSettingsComparisonMethod.WEIGHTED_AVERAGE;
    }

    public comparisonLabel() {
        return this.usesWeightedAverage() ? 'Weighted average' : 'Median';
    }

	public findCompetitorById(id: number) {
		return this.competitors.find(competitor => competitor.id === id);
	}

	public visibleCompetitors(competitorTagIds: readonly number[] = []): Competitor[] {
		if (competitorTagIds.length === 0) return this.competitors;
		return this.competitors.filter(competitor =>
			(competitor.tags ?? []).some(tag => tag.id !== undefined && competitorTagIds.includes(tag.id)));
	}

	public findCompetitorsByType(type: CompetitorType) {
		return this.competitors.filter(competitor => competitor.type === type);
	}

	public getCompetitorTags() {
		return this.competitorTags;
	}

	public orderedRoomMappingTypeConfigurations() {
		const order = Object.values(HotelRoomMappingType);
		return [...this.roomMappingTypeConfigurations].sort((a, b) => order.indexOf(a.type) - order.indexOf(b.type));
	}

	public findRoomMappingTypeName(roomMappingType: HotelRoomMappingType) {
		return Utilities.snakeCaseToWords(this.roomMappingTypeConfigurations.find(item => item.type === roomMappingType)?.name ?? roomMappingType);
	}

	public hasRateShopperDomain() {
		return this.settings.main.domains.includes(Domain.RATESHOPPER);
	}

	public hasReviewManagerDomain() {
		return this.settings.main.domains.includes(Domain.REVIEW_MANAGER);
	}

	public hasRmsDomain() {
		return this.settings.main.domains.includes(Domain.RMS);
	}

	public hasBiToolDomain() {
		return this.settings.main.domains.includes(Domain.BI_TOOL);
	}

	public hasRatesModule() {
		return this.settings.main.modules.includes(Module.RATES);
	}

	public hasParityModule() {
		return this.settings.main.modules.includes(Module.PARITY);
	}

	public hasMarketInsightModule() {
		return this.settings.main.modules.includes(Module.MARKET_INSIGHT);
	}

	public hasFlexiSetsModule() {
		return this.settings.main.modules.includes(Module.FLEXI_SETS);
	}

	public hasRatesRmsModule() {
		return this.settings.main.modules.includes(Module.RATES_RMS);
	}

	public hasPricingAutomationModule() {
		return this.settings.main.modules.includes(Module.PRICING_AUTOMATION);
	}

	public hasPriceRecommendationModule() {
		return this.settings.main.modules.includes(Module.PRICE_RECOMMENDATION);
	}

	public getAllHotelCompetitors() {
		return this.competitors.filter(competitor => competitor.type === CompetitorType.HOTEL).map(competitor => competitor.competitors[0]);
	}

	public getAllGroupCompetitors() {
		return this.competitors.filter(competitor => competitor.type === CompetitorType.GROUP).flatMap(competitor => competitor.competitors);
	}

	public hasRecommendationEngineBraintree() {
		return this.hasPriceRecommendationModule() && this.priceRecommendationSettings?.engine == CompanyHotelPriceRecommendationEngine.BRAINTRUST;
	}

	public hasRecommendationEngineRoompulseLite() {
		return this.hasPriceRecommendationModule() && this.priceRecommendationSettings?.engine === CompanyHotelPriceRecommendationEngine.ROOMPULSE && this.priceRecommendationSettings?.engineVersion === CompanyHotelPriceRecommendationEngineVersion.LITE;
	}

	public hasRecommendationEngineRoompulse() {
		return this.hasPriceRecommendationModule() && this.priceRecommendationSettings?.engine === CompanyHotelPriceRecommendationEngine.ROOMPULSE && this.priceRecommendationSettings?.engineVersion === CompanyHotelPriceRecommendationEngineVersion.NORMAL;
	}

	public hasRecommendationNormalEngineRoompulseOrBraintree() {
		return (this.hasRecommendationEngineRoompulse() || this.hasRecommendationEngineBraintree());
	}

	public hasRecommendationCanSubmitPrices() {
		return this.hasRecommendationNormalEngineRoompulseOrBraintree() && this.priceRecommendationSettings?.canSubmitPrices && this.pricingIntegrationActive === true;
	}

	public getAllHotelsWithMappings() {
		const hotels = [];
		hotels.push(this.mainHotel.competitors[0]);
		hotels.push(...this.getAllHotelCompetitors());
		return hotels;
	}

	public getAllHotels() {
		const hotels = [];
		hotels.push(this.mainHotel.competitors[0].hotel);
		this.getAllHotelCompetitors().forEach(hotel => hotels.push(hotel.hotel));
		this.getAllGroupCompetitors().forEach(hotel => hotels.push(hotel.hotel));
		return hotels;
	}

	public findHotelWithMappingsById(hotelId: number) {
		if (this.mainHotel.competitors[0].hotel.id === hotelId) return this.mainHotel.competitors[0];
		// First check hotel competitors
		const hotelCompetitor = this.getAllHotelCompetitors().find(competitor => competitor.hotel.id === hotelId);
		if (hotelCompetitor) return hotelCompetitor;
		// Then check hotels inside group competitors (FlexiSets)
		return this.getAllGroupCompetitors().find(hotel => hotel.hotel.id === hotelId);
	}

	public countUnmappedHotelRoomsFor(hotel: HotelWithRoomMappings, provider: DataProvider, isActive: boolean, isPartnerOffer: boolean) {
		return hotel.roomMappings.filter(roomMapping => roomMapping.roomMappingType === HotelRoomMappingType.NONE
			&& roomMapping.provider === provider && roomMapping.roomIsActive === isActive
			&& roomMapping.roomIsPartnerOffer === isPartnerOffer && !isSoldOutPlaceholder(roomMapping)).length;
	}

	public findParsingSettingsForProvider(provider: DataProvider) {
		return this.parsingSettings.filter(ps => ps.variables.provider === provider);
	}

	public findHotelById(hotelId: number) {
		return this.allHotels.find(hotel => hotel.id === hotelId);
	}

	public hasHotelCompetitorWithHotelId(hotelId: number) {
		return this.getAllHotelCompetitors().some(hotel => hotel.hotel.id === hotelId);
	}

	public hasBrandEnabled() {
		return this.findParsingSettingsForProvider(DataProvider.BRAND_COM)?.some(ps => ps.state === CompanyHotelParsingSettingState.ACTIVE);
	}


}
